/**
 * THE REEL'S FIGURES — every number the capability reel shows, and the arithmetic
 * that makes them agree.
 *
 * The reel is watched by accountants, and an accountant checks the figures. So
 * nothing here is typed in twice: running balances are computed from the lines,
 * the cash book from the reconciling items, the profit from the classified
 * patterns. figures.test.ts proves they agree, the way the product's own
 * arithmetic would.
 *
 * The clients are invented, and so are their figures. Amounts are minor units.
 */

export interface StatementLine { date: string; details: string; out?: number; in?: number }
export interface ReadLine extends StatementLine { balance: number }

/** Acme Trading Ltd, GBP. The reel shows the first lines of page 1 of the first statement being read, in the bank's own narrations. */
export const ACME = {
  client: "Acme Trading Ltd",
  period: "September 2026",
  currency: "GBP",
  openingBalance: 1_824_055,
  firstStatement: "1–15 Sep",
  secondStatement: "20–30 Sep",
  gap: "16–19 Sep",
  statementLines: 211,
  matched: 209,
  gapCashBookLines: 2,
  lines: [
    { date: "01 Sep", details: "CARD OFFICE SUPPLY", out: 8_640 },
    { date: "02 Sep", details: "TFR NORTHWIND FOODS", in: 420_000 },
    { date: "02 Sep", details: "DD CITY POWER", out: 31_218 },
    { date: "03 Sep", details: "CHQ 104512", out: 115_000 },
    { date: "03 Sep", details: "CARD FUEL STATION", out: 6_420 },
    { date: "04 Sep", details: "TFR HARBOR FREIGHT", out: 248_000 },
    { date: "04 Sep", details: "CARD STATIONERY HSE", out: 2_375 },
  ] as StatementLine[],
  /** Balance per the second statement at 30 September. */
  statementClosing: 2_190_610,
  unpresentedCheque: { ref: "Cheque 104530", amount: 64_000 },
  bankCharge: 1_850,
  interest: 214,
  duplicateReceipt: { from: "NORTHWIND FOODS", amount: 420_000 },
} as const;

/** The statement as Leveri reads it: each line with the balance its own arithmetic gives. */
export function readStatement(opening: number, lines: readonly StatementLine[]): ReadLine[] {
  let balance = opening;
  return lines.map((l) => {
    balance += (l.in ?? 0) - (l.out ?? 0);
    return { ...l, balance };
  });
}

/**
 * The reconciliation at 30 September. The cash book's balance is what the named
 * items say it must be, so the difference is explained in full and never plugged.
 */
export function acmeReconciliation() {
  const adjustedBank = ACME.statementClosing - ACME.unpresentedCheque.amount;
  // Adjusted cash book = cash book + interest − charge − the receipt entered twice; it equals the adjusted bank.
  const cashBook = adjustedBank - ACME.interest + ACME.bankCharge + ACME.duplicateReceipt.amount;
  return { statement: ACME.statementClosing, cashBook, difference: cashBook - ACME.statementClosing, adjustedBank };
}

export interface Pattern { key: string; pattern: string; lines: number; amount: number; account: string; how: "RULE" | "SUGGESTED" | "INVOICES" | "ASKED"; side: "IN" | "OUT" }

/** Harbour Kitchen Ltd, NGN, year to 31 March 2026: the bank lines by pattern. */
export const HARBOUR = {
  client: "Harbour Kitchen Ltd",
  period: "Year to 31 March 2026",
  currency: "NGN",
  patterns: [
    { key: "pos", pattern: "POS settlement · CARDPOINT", lines: 412, amount: 4_821_530_000, account: "Sales", how: "RULE", side: "IN" },
    { key: "farm", pattern: "Transfer out · FRESH FARMS SUPPLY", lines: 96, amount: 1_988_000_000, account: "Cost of sales", how: "SUGGESTED", side: "OUT" },
    { key: "rent", pattern: "Transfer out · PRIME PROPERTIES", lines: 4, amount: 600_000_000, account: "Rent", how: "INVOICES", side: "OUT" },
    { key: "power", pattern: "Direct debit · CITY POWER", lines: 12, amount: 241_050_000, account: "Utilities", how: "SUGGESTED", side: "OUT" },
    { key: "fuel", pattern: "Card payment · fuel", lines: 58, amount: 196_420_000, account: "Motor expenses", how: "RULE", side: "OUT" },
    { key: "unknown", pattern: "Transfer out · no name given", lines: 3, amount: 125_000_000, account: "Suspense", how: "ASKED", side: "OUT" },
  ] as Pattern[],
} as const;

/** The draft profit and loss from the classified patterns. Suspense is held aside, never in the profit. */
export function harbourProfitAndLoss() {
  const sum = (accounts: string[]) => HARBOUR.patterns.filter((p) => accounts.includes(p.account)).reduce((s, p) => s + p.amount, 0);
  const revenue = sum(["Sales"]);
  const costOfSales = sum(["Cost of sales"]);
  const overheads = [
    { label: "Rent", amount: sum(["Rent"]) },
    { label: "Utilities", amount: sum(["Utilities"]) },
    { label: "Motor expenses", amount: sum(["Motor expenses"]) },
  ];
  const grossProfit = revenue - costOfSales;
  const overheadTotal = overheads.reduce((s, o) => s + o.amount, 0);
  return { revenue, costOfSales, grossProfit, overheads, overheadTotal, netProfit: grossProfit - overheadTotal, suspense: sum(["Suspense"]) };
}

/** 2190610 → "21,906.10". Negative amounts are shown in brackets, as a schedule shows them. */
export function money(minor: number): string {
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / 100).toLocaleString("en-GB");
  const text = `${whole}.${String(abs % 100).padStart(2, "0")}`;
  return minor < 0 ? `(${text})` : text;
}
